<template>
  <div class="w-full mb-5">
    <label
      :for="uniqueId"
      class="block text-sm font-medium leading-5 text-gray-700 mb-1"
      v-text="label"
    ></label>
    <div class="mt-1 relative">
      <div
        v-if="prepend"
        class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"
      >
        <span class="text-gray-500 sm:text-sm sm:leading-5">
          {{ prepend }}
        </span>
      </div>
      <input
        :id="uniqueId"
        :type="type"
        :placeholder="placeholder"
        class="appearance-none border rounded w-full py-2 px-3
               text-gray-700 leading-tight focus:outline-none focus:shadow-outline-green"
        :class="{'border-red-600': error != null, 'pl-7': prepend, 'pr-12': append}"
        :value="value"
        @input="onInput"
        @change="onChange"
      />

      <transition name="fade-fast">
        <div
          v-if="append && error == null"
          class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none"
        >
          <span class="text-gray-500 sm:text-sm sm:leading-5">
            {{ append }}
          </span>
        </div>
      </transition>
      <transition name="fade-fast">
        <div
          v-if="error != null"
          class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none"
        >
          <svg
            class="h-5 w-5 text-red-500"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fill-rule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0
              00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
              clip-rule="evenodd"
            />
          </svg>
        </div>
      </transition>
    </div>
    <transition name="fade-fast" mode="out-in">
      <p
        v-if="hint && error == null"
        :key="`${uniqueId}_hint`"
        class="mt-2 text-sm text-gray-500"
        v-text="hint"
      ></p>
      <p
        v-if="error != null"
        :key="`${uniqueId}_error`"
        class="mt-2 text-sm text-red-600"
        v-text="error"
      ></p>
    </transition>
  </div>
</template>

<script>
import Utils from '../_mixins/Utils';

export default {
  name: 'TextInput',
  mixins: [Utils],
  props: {
    type: {
      type: String,
      default: () => 'text',
    },
    value: {
      type: [String, Number],
      default: () => null,
    },
    name: {
      type: String,
      default: () => null,
    },
    label: {
      type: String,
      default: () => null,
    },
    placeholder: {
      type: String,
      default: () => null,
    },
    hint: {
      type: String,
      default: () => null,
    },
    rules: {
      type: Array,
      default: () => [],
    },
    prepend: {
      type: String,
      default: () => '',
    },
    append: {
      type: String,
      default: () => '',
    },
  },
  data() {
    return {
      typingTimeout: null,
      valid: true,
      error: null,
    };
  },
  methods: {
    onInput(event) {
      this.$emit('input', event.target.value);
      this.$nextTick(() => {
        this.validateInput();
      });
    },
    onChange(event) {
      this.$emit('change', event.target.value);
      this.$nextTick(() => {
        this.validateInput();
      });
    },
    validateInput() {
      return this.rules.forEach((rule, index) => {
        const result = rule(this.value);
        // If we hit a function that returned a string instead of true
        // Then we set the error to that string and break the loop
        if (result !== true) {
          this.error = result;
          this.$emit('validation:Error', {
            error: result,
            value: this.value,
          });
          return false;
        }
        // If we've hit this point there's no errors
        if (index === this.rules.length - 1) {
          this.error = null;
          this.$emit('validation:Success', this.value);
        }
        return true;
      });
    },
  },
};
</script>

<style>
  input::placeholder {
    color:#d2d6dc;
  }
  input::-moz-placeholder {
    color:#d2d6dc;
  }
  input:-ms-input-placeholder {
    color:#d2d6dc;
  }
  input::-webkit-input-placeholder {
    color:#d2d6dc;
  }
  input::-webkit-outer-spin-button,
  input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  input[type=number] {
    -moz-appearance: textfield;
  }
</style>
